import {
  CheckmarkFill18Icon,
  HeadphonesOutline24Icon,
  LogOutOutline18Icon,
  PauseMediaControllerFill18Icon,
} from "@vygruppen/spor-icon-react";
import { useFeatureFlag } from "app/FeatureFlags/useFeatureFlags";
import { PUZZEL_OIDC_LOGIN_FLAG } from "app/PuzzelAuth/puzzelAuthConfig";
import {
  HorizontalContainer,
  ProfileDetails,
  StatusMenuItem,
} from "features/Header/PuzzelTelephone/components/Logon/StyledLogonComponents";
import { useCallStatus } from "features/Header/PuzzelTelephone/provider/contexts/CallStatusContext";
import { usePuzzelExtendedStateInformation } from "features/Header/PuzzelTelephone/provider/contexts/ListenForCallStatusContext";
import { usePuzzelAuthenticationContext } from "features/Header/PuzzelTelephone/provider/contexts/PuzzelAuthenticationContext";
import {
  mapResultToPresenceStatus,
  usePerformAgentPauseOff,
  usePerformAgentPauseOn,
  usePerformUserLogOnOff,
  useUserDetailsState,
} from "features/Header/PuzzelTelephone/provider/contexts/UserDetailsAndLogOnOffContext";
import { useUserValidationState } from "features/Header/PuzzelTelephone/provider/contexts/UserValidationContext";
import {
  removePhoneNumberPrefix,
  validPhoneNumber,
} from "features/Header/PuzzelTelephone/utils";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SkeletonButton } from "shared/components/feedback/SkeletonLoader/SkeletonLoaderStyles";
import { Dropdown } from "shared/components/forms/Dropdown";
import { InputField } from "shared/components/forms/Inputfield";
import { Select } from "shared/components/forms/Select";
import { DropsButton } from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import styled, { useTheme } from "styled-components";

type PuzzelProfile = {
  groupProfile: boolean;
  id: string;
  name: string;
  number: number;
  phoneType: string;
  qualities: Array<unknown>;
  phoneNumber?: string;
  lastPhoneNumber?: string;
};

type SelectItem = {
  value: string;
  text: string;
};

// "Velg et alternativ" is 18 characters
const ProfileContainer = styled.div<{ $longestLabel: number | undefined }>`
  button > :first-child > :first-child {
    transform: scale(0.8) translateY(-2px) translateX(-5px);
  }
  min-width: ${({ $longestLabel }) =>
    `${Math.max($longestLabel ?? 18, 18) + 5}ch`};
`;

const Logon = () => {
  const {
    phoneNumber: nonPrefixedPhoneNumber,
    profileId,
    setPhoneNumber,
    setProfileId,
  } = useCallStatus();

  const { t } = useTranslation();
  const theme = useTheme();
  const { enabled: oidcEnabled } = useFeatureFlag(PUZZEL_OIDC_LOGIN_FLAG);
  const userDetails = useUserDetailsState();
  const { userStateInformation } = usePuzzelExtendedStateInformation();
  const { phoneNumberPrefix } = usePuzzelAuthenticationContext();
  const { userId } = useUserValidationState();
  const performUserLogOnOff = usePerformUserLogOnOff();
  const performAgentPauseOn = usePerformAgentPauseOn();
  const performAgentPauseOff = usePerformAgentPauseOff();

  const {
    currentProfile,
    profiles,
    isLoggedOn,
    presenceStatus,
    pauseTypes,
    hasRealPauseTypes,
    isPresencePending,
  } = userDetails;
  const enableBtn = profileId && validPhoneNumber(nonPrefixedPhoneNumber);

  // userStateInformation is polled every 2s (ListenForCallStatusContext) so it picks up status
  // changes made elsewhere (e.g. the native Puzzel Agent app) - prefer it over the one-shot
  // presenceStatus from login/our own actions, which only updates right after our own calls.
  const effectivePresenceStatus = userStateInformation?.contactCentreStatus
    ? mapResultToPresenceStatus(userStateInformation.contactCentreStatus)
    : presenceStatus;

  const [selectedPauseTypeId, setSelectedPauseTypeId] = useState<
    number | undefined
  >(undefined);

  const AVAILABLE_VALUE = "available";
  const LOGOFF_VALUE = "loggoff";

  const handlePresenceChange = useCallback(
    (value: string) => {
      if (value === LOGOFF_VALUE) {
        performUserLogOnOff({
          param: { userId, logOnOrOff: "logoff" },
          payload: {},
        });
        return;
      }
      if (value === AVAILABLE_VALUE) {
        performAgentPauseOff();
        return;
      }
      // Without real pause types configured for this account, go into a generic pause with no
      // reason selected - matches native Puzzel Agent's own request (empty body, no pauseTypeId).
      if (!hasRealPauseTypes) {
        performAgentPauseOn();
        return;
      }
      const pauseTypeId = Number(value);
      setSelectedPauseTypeId(pauseTypeId);
      performAgentPauseOn(pauseTypeId);
    },
    [
      performAgentPauseOn,
      performAgentPauseOff,
      performUserLogOnOff,
      userId,
      hasRealPauseTypes,
    ],
  );

  const currentPauseType = pauseTypes.find(
    (pauseType) => pauseType.id === (selectedPauseTypeId ?? pauseTypes[0]?.id),
  );
  const statusLabel =
    effectivePresenceStatus === "paused"
      ? (currentPauseType?.description ?? t("Pause"))
      : t("Ledig");
  const statusIcon =
    effectivePresenceStatus === "paused" ? (
      <PauseMediaControllerFill18Icon color={theme.colorWarning} />
    ) : (
      <CheckmarkFill18Icon color={theme.colorSuccess} />
    );

  const items: SelectItem[] | undefined = useMemo(
    () =>
      profiles?.map((profile: PuzzelProfile) => ({
        text: profile.name,
        value: profile.id,
      })),
    [profiles],
  );

  const longestLabel = useMemo(
    () =>
      items?.reduce(
        (prev, curr) => (curr.text.length > prev ? curr.text.length : prev),
        0,
      ),
    [items],
  );

  const logOn = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      performUserLogOnOff({
        param: {
          userId,
          logOnOrOff: "logon",
        },
        payload: {
          phoneNumber: phoneNumberPrefix + nonPrefixedPhoneNumber,
          profileId,
        },
      });
    },
    [
      performUserLogOnOff,
      userId,
      phoneNumberPrefix,
      nonPrefixedPhoneNumber,
      profileId,
    ],
  );

  // Legacy-only: OIDC moves "Logg av" into the status dropdown instead (handlePresenceChange).
  // TODO: remove once puzzelOidcLogin has rolled out fully.
  const logOff = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      performUserLogOnOff({
        param: {
          userId,
          logOnOrOff: "logoff",
        },
        payload: {},
      });
    },
    [performUserLogOnOff, userId],
  );

  useEffect(() => {
    if (currentProfile?.id) {
      setProfileId(currentProfile?.id);
    }
  }, [currentProfile?.id]);

  if (isLoggedOn === null) {
    return <SkeletonButton />;
  }

  if (
    !isLoggedOn ||
    userStateInformation?.contactCentreStatus === "LoggedOff"
  ) {
    return (
      <HorizontalContainer onSubmit={logOn}>
        <ProfileContainer $longestLabel={longestLabel}>
          <Select
            name="profileId"
            label="Telefonkø"
            value={profileId}
            onChange={(e) => {
              const { value } = e.target;
              if (typeof value !== "string") return;
              setProfileId(value);
            }}
          >
            {items?.map((item) => (
              <option value={item.value} key={item.value}>
                {item.text}
              </option>
            ))}
          </Select>
        </ProfileContainer>

        <InputField
          type="text"
          onChange={(event) => {
            setPhoneNumber(event.target.value);
          }}
          value={nonPrefixedPhoneNumber}
          placeholder="Telefonnummer"
        />
        <DropsButton
          ml="12px"
          size="sm"
          variant="secondary"
          type="submit"
          disabled={!enableBtn}
          leftIcon={<HeadphonesOutline24Icon />}
        >
          {t("Logg på")}
        </DropsButton>
      </HorizontalContainer>
    );
  }

  if (!oidcEnabled) {
    return (
      <HorizontalContainer onSubmit={logOff}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              marginRight: "1rem",
            }}
          >
            <Text>{currentProfile?.name}</Text>
            <Text>
              (
              {removePhoneNumberPrefix(
                currentProfile.phoneNumber,
                phoneNumberPrefix,
              )}
              )
            </Text>
          </div>
          <DropsButton
            size="sm"
            variant="secondary"
            type="submit"
            leftIcon={<HeadphonesOutline24Icon />}
          >
            {t("Logg av")}
          </DropsButton>
        </div>
      </HorizontalContainer>
    );
  }

  return (
    <HorizontalContainer as="div">
      <ProfileDetails>
        <Text>{currentProfile?.name}</Text>
        <Text>
          (
          {removePhoneNumberPrefix(
            currentProfile.phoneNumber,
            phoneNumberPrefix,
          )}
          )
        </Text>
      </ProfileDetails>
      <Dropdown
        label={statusLabel}
        icon={statusIcon}
        width="170px"
        disabled={isPresencePending}
      >
        <StatusMenuItem
          value={AVAILABLE_VALUE}
          leftIcon={<CheckmarkFill18Icon color={theme.colorSuccess} />}
          onClick={() => handlePresenceChange(AVAILABLE_VALUE)}
        >
          {t("Ledig")}
        </StatusMenuItem>
        {pauseTypes.map((pauseType) => (
          <StatusMenuItem
            key={pauseType.id}
            value={String(pauseType.id)}
            leftIcon={
              <PauseMediaControllerFill18Icon color={theme.colorWarning} />
            }
            onClick={() => handlePresenceChange(String(pauseType.id))}
          >
            {pauseType.description}
          </StatusMenuItem>
        ))}
        <StatusMenuItem
          value={LOGOFF_VALUE}
          leftIcon={<LogOutOutline18Icon />}
          onClick={() => handlePresenceChange(LOGOFF_VALUE)}
        >
          {t("Logg av")}
        </StatusMenuItem>
      </Dropdown>
    </HorizontalContainer>
  );
};

export default Logon;
